// CustomerModal — criar/editar customer. Reusa EmojiInput pro icon picker.
//
// 2026-05-18: Workspace de Clientes. Modal compacto com preview ao vivo da
// identidade visual (icon + cor + nome em chip).

import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import { customersApi, type CustomerDTO } from '../lib/api';
import { EmojiInput } from './EmojiInput';
import { dialog } from './Dialog';

interface Props {
  customer?: CustomerDTO | null;  // null/undefined = modo create
  onClose: () => void;
  onSaved: (customer: CustomerDTO) => void;
}

const DEFAULT_COLOR = '#6366f1';

// Sugestões de cores rápidas — paleta enxuta + acessível
const PRESET_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#06b6d4', '#84cc16', '#f97316',
];

// Slugify simples (mirror leve do helper backend pra preview live)
function slugify(s: string): string {
  return s.toLowerCase().trim()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function isValidHex(s: string): boolean {
  return /^#[0-9a-f]{3}([0-9a-f]{3})?([0-9a-f]{2})?$/i.test(s);
}

export function CustomerModal({ customer, onClose, onSaved }: Props) {
  const isEdit = !!customer;

  const [name, setName] = useState(customer?.name ?? '');
  const [slug, setSlug] = useState(customer?.slug ?? '');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(isEdit);
  const [icon, setIcon] = useState(customer?.icon ?? '👤');
  const [color, setColor] = useState(customer?.color ?? DEFAULT_COLOR);
  const [contactEmail, setContactEmail] = useState(customer?.contact_email ?? '');
  const [contactPhone, setContactPhone] = useState(customer?.contact_phone ?? '');
  const [notes, setNotes] = useState(customer?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);

  // 2026-05-19 Phase 7: dirty-state detector pra confirm dialog antes
  // de fechar com mudanças não salvas (resolve perda silenciosa de edit).
  const isDirty = useMemo(() => {
    const baseName = customer?.name ?? '';
    const baseSlug = customer?.slug ?? '';
    const baseIcon = customer?.icon ?? '👤';
    const baseColor = customer?.color ?? DEFAULT_COLOR;
    const baseEmail = customer?.contact_email ?? '';
    const basePhone = customer?.contact_phone ?? '';
    const baseNotes = customer?.notes ?? '';
    return name !== baseName
      || slug !== baseSlug
      || icon !== baseIcon
      || color !== baseColor
      || contactEmail !== baseEmail
      || contactPhone !== basePhone
      || notes !== baseNotes;
  }, [customer, name, slug, icon, color, contactEmail, contactPhone, notes]);

  // Wrapper que confirma antes de fechar quando há mudanças.
  const safeClose = useCallback(async () => {
    if (!isDirty || saving) {
      onClose();
      return;
    }
    const ok = await dialog.confirm(
      'Você tem mudanças não salvas. Sair mesmo assim?',
      { destructive: true, confirmLabel: 'Descartar' },
    );
    if (ok) onClose();
  }, [isDirty, saving, onClose]);

  // Auto-gen slug from name quando não foi editado manualmente
  useEffect(() => {
    if (!slugManuallyEdited) setSlug(slugify(name));
  }, [name, slugManuallyEdited]);

  // Debounce slug availability check (350ms)
  useEffect(() => {
    if (!slug || isEdit && slug === customer?.slug) {
      setSlugAvailable(null);
      return;
    }
    setSlugChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await customersApi.checkSlug(slug);
        setSlugAvailable(r.available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setSlugChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [slug, isEdit, customer?.slug]);

  // ESC fecha (com confirm se dirty)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') safeClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [safeClose]);

  // Contraste texto pro chip preview — YIQ
  const textOnColor = useMemo(() => {
    if (!isValidHex(color)) return '#fff';
    const hex = color.slice(1);
    if (hex.length < 6) return '#fff';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? '#0f172a' : '#fff';
  }, [color]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setErr('Nome é obrigatório'); return; }
    if (!isEdit && slugAvailable === false) { setErr('Slug já está em uso'); return; }
    setSaving(true); setErr(null);
    try {
      const payload = {
        name: name.trim(),
        slug: slug.trim() || undefined,
        icon: icon.trim() || null,
        color: isValidHex(color) ? color : null,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
        notes: notes.trim() || null,
      };
      const r = isEdit
        ? await customersApi.update(customer!.id, payload)
        : await customersApi.create(payload);
      onSaved(r.customer);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={safeClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">
            {isEdit ? 'Editar cliente' : 'Novo cliente'}
            {isDirty && (
              <span className="ml-2 text-[10px] font-normal text-amber-600">• não salvo</span>
            )}
          </h2>
          <button type="button" onClick={safeClose} className="text-gray-400 hover:text-gray-700 p-1">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Identidade visual: icon + color picker + preview */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Identidade visual
            </label>
            <div className="flex items-center gap-3">
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] text-gray-500">Ícone</span>
                <EmojiInput
                  value={icon}
                  onChange={setIcon}
                  inputClassName="w-16 text-center text-2xl border border-gray-200 rounded-lg py-2 focus:outline-none focus:border-indigo-400"
                  buttonSize={16}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] text-gray-500">Cor</span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="color"
                    value={isValidHex(color) ? color.slice(0, 7) : DEFAULT_COLOR}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer"
                  />
                  <div className="flex flex-wrap gap-1 max-w-[8rem]">
                    {PRESET_COLORS.slice(0, 5).map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setColor(c)}
                        className="w-4 h-4 rounded-full border border-gray-200 hover:scale-110 transition-transform"
                        style={{ background: c }}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {/* Preview chip */}
              <div className="flex-1 flex justify-end">
                <div
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-l-4"
                  style={{
                    background: isValidHex(color) ? color + '15' : '#f3f4f6',
                    borderColor: isValidHex(color) ? color : '#d1d5db',
                    color: textOnColor === '#fff' && isValidHex(color) ? color : '#0f172a',
                  }}
                >
                  <span className="text-lg">{icon}</span>
                  <span>{name || 'Nome do cliente'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Nome + slug */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Nome <span className="text-red-500">*</span>
            </label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: João Restaurantes"
              required
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="flex items-center justify-between text-xs font-semibold text-gray-700 mb-1">
              <span>Slug (URL)</span>
              {!slugManuallyEdited && (
                <span className="text-[10px] text-gray-400 font-normal">auto-gerado</span>
              )}
            </label>
            <div className="flex items-center gap-1.5">
              <input
                value={slug}
                onChange={(e) => { setSlug(slugify(e.target.value)); setSlugManuallyEdited(true); }}
                placeholder="joao-restaurantes"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-indigo-400"
              />
              {slugManuallyEdited && (
                <button
                  type="button"
                  onClick={() => { setSlugManuallyEdited(false); setSlug(slugify(name)); }}
                  className="text-[10px] text-indigo-600 hover:text-indigo-800 px-2 py-1"
                  title="Auto-gerar pelo nome"
                >
                  ↻
                </button>
              )}
            </div>
            <div className="mt-1 text-[11px] min-h-[16px]">
              {slugChecking ? (
                <span className="text-gray-400 inline-flex items-center gap-1">
                  <Loader2 size={10} className="animate-spin" /> Verificando…
                </span>
              ) : slugAvailable === true ? (
                <span className="text-emerald-600">✓ disponível</span>
              ) : slugAvailable === false ? (
                <span className="text-red-600">✕ já está em uso</span>
              ) : null}
            </div>
          </div>

          {/* Contato */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">📧 Email</label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="contato@cliente.com"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">📞 Telefone</label>
              <input
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="(11) 99999-9999"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
              />
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">📋 Notas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações sobre o cliente, projetos, contatos..."
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 resize-y"
            />
          </div>

          {/* Error */}
          {err && (
            <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={safeClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 rounded-lg inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Salvar' : 'Criar →'}
          </button>
        </div>
      </form>
    </div>
  );
}
